import { theme } from '@/_theme'
import { ApplicationStatusSchema, type TApplicationStatus } from '@doutorsim/shared/applications'
import type { TUserType } from '../_lib/auth-client.types'

type TStatusConfig = {
  label: string
  description: string
  color: string
  badgeClassName: string
  badgeCircleClassName: string
  isActionNeeded?: boolean
  isInPipeline?: boolean
}

const BLUE: Pick<TStatusConfig, 'color' | 'badgeClassName' | 'badgeCircleClassName'> = {
  color: theme.colors.aux.blue.info[6],
  badgeClassName: 'rounded-xl bg-aux-blue-info-1 text-aux-blue-info-6',
  badgeCircleClassName: 'bg-aux-blue-info-6',
}

const YELLOW: Pick<TStatusConfig, 'color' | 'badgeClassName' | 'badgeCircleClassName'> = {
  color: theme.colors.aux.yellow.warning[6],
  badgeClassName: 'rounded-xl bg-aux-yellow-warning-1 text-aux-yellow-warning-6',
  badgeCircleClassName: 'bg-aux-yellow-warning-6',
}

const GREEN: Pick<TStatusConfig, 'color' | 'badgeClassName' | 'badgeCircleClassName'> = {
  color: theme.colors.brand.green.primary[5],
  badgeClassName: 'rounded-xl bg-brand-green-primary-1 text-brand-green-primary-5',
  badgeCircleClassName: 'bg-brand-green-primary-5',
}

const GRAY: Pick<TStatusConfig, 'color' | 'badgeClassName' | 'badgeCircleClassName'> = {
  color: theme.colors.brand.grayscale[6],
  badgeClassName: 'rounded-xl bg-brand-grayscale-3 text-brand-grayscale-7',
  badgeCircleClassName: 'bg-brand-grayscale-7',
}

const RED: Pick<TStatusConfig, 'color' | 'badgeClassName' | 'badgeCircleClassName'> = {
  color: theme.colors.aux.red.danger[6],
  badgeClassName: 'rounded-xl bg-aux-red-danger-1 text-aux-red-danger-6',
  badgeCircleClassName: 'bg-aux-red-danger-6',
}

// ---------------------------------------------------------------------------
// Staff config — single source of truth for every status
// Blue = client acts (aguardando)  | Yellow = staff acts (infinitive verb)
// Green = success terminal         | Gray = rejected | Red = cancelled/expired
// ---------------------------------------------------------------------------

const STAFF_STATUS_CONFIG: Record<TApplicationStatus, TStatusConfig> = {
  'pending-analysis': {
    label: 'Analisar crédito',
    description: 'Analise o crédito do responsável.',
    ...YELLOW,
    isActionNeeded: true,
    isInPipeline: true,
  },
  'pending-guarantor': {
    label: 'Aguardando avalista',
    description: 'Aguardando os dados do avalista.',
    ...BLUE,
    isInPipeline: true,
  },
  'pending-handoff': {
    label: 'Aguardando liberação',
    description: 'Análise aprovada. Aguardando a instituição liberar o envio da proposta.',
    ...BLUE,
    isInPipeline: true,
  },
  'pending-acceptance': {
    label: 'Aguardando aceite',
    description: 'Análise aprovada. Aguardando o aceite do cliente.',
    ...BLUE,
    isInPipeline: true,
  },
  'pending-new-proposal': {
    label: 'Revisar proposta',
    description: 'Avalie criar uma nova proposta.',
    ...YELLOW,
    isActionNeeded: true,
    isInPipeline: true,
  },
  'pending-declarations': {
    label: 'Confirmação cadastral',
    description: 'Aguardando o cliente enviar os dados cadastrais.',
    ...BLUE,
    isInPipeline: true,
  },
  'pending-declarations-confirmation': {
    label: 'Verificar dados',
    description: 'Verifique os dados com as fontes oficiais.',
    ...YELLOW,
    isActionNeeded: true,
    isInPipeline: true,
  },
  'pending-signature': {
    label: 'Aguardando assinatura',
    description: 'Aguardando a assinatura do contrato pelo cliente.',
    ...BLUE,
    isInPipeline: true,
  },
  'pending-disbursement': {
    label: 'Processar desembolso',
    description: 'Contrato assinado. Processe o desembolso.',
    ...YELLOW,
    isActionNeeded: true,
    isInPipeline: true,
  },
  active: { label: 'Ativo', description: 'Contrato ativo', ...GREEN },
  settled: { label: 'Quitado', description: 'Contrato quitado', ...GREEN },
  rejected: { label: 'Reprovado', description: 'Crédito reprovado na análise.', ...GRAY },
  cancelled: { label: 'Cancelado', description: 'Solicitação cancelada', ...RED },
  expired: { label: 'Expirado', description: 'Solicitação expirada por inatividade.', ...RED },
}

// ---------------------------------------------------------------------------
// Tenant config — groups multiple internal statuses into client-facing labels
// Blue = processing (em/gerund) | Green = success terminal | Gray = any terminal
// ---------------------------------------------------------------------------

const TENANT_STATUS_GROUPS: Array<{
  statuses: [TApplicationStatus, ...TApplicationStatus[]]
  config: TStatusConfig
}> = [
  {
    statuses: ['pending-analysis'],
    config: {
      label: 'Em análise',
      description: 'Estamos analisando o crédito do paciente.',
      ...BLUE,
    },
  },
  {
    statuses: ['pending-guarantor'],
    config: {
      label: 'Aguardando avalista',
      description: 'Aguardando os dados do avalista do paciente.',
      ...BLUE,
    },
  },
  {
    statuses: ['pending-handoff'],
    config: {
      label: 'Contatar paciente',
      description:
        'Crédito aprovado. Contate o paciente e libere o envio da proposta pelo WhatsApp.',
      ...YELLOW,
    },
  },
  {
    statuses: ['pending-acceptance', 'pending-new-proposal', 'pending-declarations'],
    config: {
      label: 'Aprovado',
      description:
        'Crédito aprovado. Aguardando o paciente aceitar a proposta e confirmar os dados.',
      ...GREEN,
    },
  },
  {
    statuses: ['pending-declarations-confirmation'],
    config: {
      label: 'Verificando dados',
      description: 'Conferindo os dados informados pelo paciente.',
      ...BLUE,
    },
  },
  {
    statuses: ['pending-signature'],
    config: {
      label: 'Aguardando assinatura',
      description: 'Contrato enviado. Aguardando a assinatura do paciente.',
      ...BLUE,
    },
  },
  {
    statuses: ['pending-disbursement'],
    config: {
      label: 'Processando',
      description: 'Contrato assinado. Liberando o valor do crédito.',
      ...BLUE,
    },
  },
  {
    statuses: ['active', 'settled'],
    config: { label: 'Liberado', description: 'Crédito liberado com sucesso.', ...GREEN },
  },
  {
    statuses: ['rejected'],
    config: { label: 'Reprovado', description: 'Crédito não aprovado na análise.', ...GRAY },
  },
  {
    statuses: ['cancelled'],
    config: { label: 'Cancelado', description: 'A solicitação foi cancelada.', ...GRAY },
  },
  {
    statuses: ['expired'],
    config: { label: 'Expirado', description: 'A solicitação expirou por inatividade.', ...GRAY },
  },
]

const TENANT_STATUS_CONFIG: Record<TApplicationStatus, TStatusConfig> = Object.fromEntries(
  TENANT_STATUS_GROUPS.flatMap((group) => group.statuses.map((status) => [status, group.config])),
) as Record<TApplicationStatus, TStatusConfig>

// Maps every raw status to the representative status of its tenant group — the
// id the tenant kanban/board uses as its column key (see getStatusOptions). The
// tenant board only renders one column per group keyed by its first status, so
// cards must be bucketed by this id (not the raw status) or they vanish.
const RAW_STATUS_TO_TENANT_COLUMN = Object.fromEntries(
  TENANT_STATUS_GROUPS.flatMap((group) =>
    group.statuses.map((status) => [status, group.statuses[0]]),
  ),
) as Record<TApplicationStatus, TApplicationStatus>

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getStatusConfig(status: TApplicationStatus, userType: TUserType): TStatusConfig {
  const config = userType === 'staff' ? STAFF_STATUS_CONFIG : TENANT_STATUS_CONFIG
  return config[status]
}

// The kanban column id a given application belongs to. Staff columns are the
// raw statuses; tenant columns group several raw statuses under the group's
// representative (first) status.
export function getKanbanColumnId(
  status: TApplicationStatus,
  userType: TUserType,
): TApplicationStatus {
  if (userType === 'staff') return status
  return RAW_STATUS_TO_TENANT_COLUMN[status] ?? status
}

export function getStatusOptions(
  userType: TUserType,
): Array<{ value: TApplicationStatus; label: string }> {
  if (userType === 'staff') {
    return ApplicationStatusSchema.options.map((status) => ({
      value: status,
      label: STAFF_STATUS_CONFIG[status].label,
    }))
  }

  return TENANT_STATUS_GROUPS.map((group) => ({
    value: group.statuses[0],
    label: group.config.label,
  }))
}

export function getActionNeededStatuses(): TApplicationStatus[] {
  return ApplicationStatusSchema.options.filter(
    (status) => STAFF_STATUS_CONFIG[status].isActionNeeded,
  )
}

export function getInPipelineStatuses(): TApplicationStatus[] {
  return ApplicationStatusSchema.options.filter(
    (status) => STAFF_STATUS_CONFIG[status].isInPipeline,
  )
}

export function getTenantActiveStatuses(): TApplicationStatus[] {
  return TENANT_STATUS_GROUPS.filter(
    (g) => !['rejected', 'cancelled', 'expired'].includes(g.statuses[0]),
  ).map((g) => g.statuses[0])
}

export function getStatusesForFilter(
  value: TApplicationStatus,
  userType: TUserType,
): TApplicationStatus[] {
  if (userType === 'staff') {
    return [value]
  }

  const group = TENANT_STATUS_GROUPS.find((g) => g.statuses[0] === value)
  return group ? group.statuses : [value]
}
